import { createContext, useContext, useMemo, type PropsWithChildren } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createApiClient, type ApiClient, type CreateApiClientOptions } from "../client";
import type { TokenProvider } from "../auth-bridge/tokenProvider";

interface ApiContextValue {
  client: ApiClient;
  baseUrl: string;
  /**
   * Kept on context so the `useUploadMultipart` hook in `hooks/documents.ts`
   * (and future multipart consumers) can reach the access token + origin
   * without re-plumbing them. Mutlipart uploads route through the XHR
   * `uploadMultipart` helper in `client.ts` rather than openapi-fetch —
   * the helper needs both.
   */
  tokenProvider: TokenProvider;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export interface ApiProviderProps extends PropsWithChildren<CreateApiClientOptions> {
  /**
   * Optional. If omitted, a default `QueryClient` is created with HR-admin-friendly
   * defaults (see below). Apps that already own a `QueryClient` can pass it in —
   * common when they need Devtools / SSR hydration / custom persistence.
   */
  queryClient?: QueryClient;
}

/**
 * Default QueryClient config:
 *   - `staleTime` 30s       — balances freshness vs network load. Override per-query.
 *   - `gcTime` 5min         — keep inactive data around for quick back-nav.
 *   - `retry` (5xx-only)    — ApiError with `status >= 500` retries up to 2 times;
 *                             4xx (user / validation) never retry.
 *   - `refetchOnWindowFocus: false`  — HR screens don't benefit from aggressive refetch.
 */
function createDefaultQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, err) => {
          if (
            err &&
            typeof err === "object" &&
            "status" in err &&
            typeof (err as { status: unknown }).status === "number"
          ) {
            const status = (err as { status: number }).status;
            return status >= 500 && failureCount < 2;
          }
          return failureCount < 1;
        },
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Wrap your app with `<ApiProvider tokenProvider={...} baseUrl={...}>` to inject
 * the api client and a `QueryClient` with tenant-flush-safe defaults.
 *
 * @example
 *   <ApiProvider tokenProvider={authService} baseUrl={env.VITE_API_BASE_URL}>
 *     <App />
 *   </ApiProvider>
 */
export function ApiProvider({
  children,
  tokenProvider,
  baseUrl,
  fetch,
  setBillingWarning,
  queryClient: qcProp,
}: ApiProviderProps) {
  const client = useMemo(
    () =>
      createApiClient({
        tokenProvider,
        baseUrl,
        ...(fetch ? { fetch } : {}),
        ...(setBillingWarning ? { setBillingWarning } : {}),
      }),
    [tokenProvider, baseUrl, fetch, setBillingWarning],
  );
  const qc = useMemo(() => qcProp ?? createDefaultQueryClient(), [qcProp]);
  const ctx = useMemo<ApiContextValue>(
    () => ({ client, baseUrl, tokenProvider }),
    [client, baseUrl, tokenProvider],
  );

  return (
    <QueryClientProvider client={qc}>
      <ApiContext.Provider value={ctx}>{children}</ApiContext.Provider>
    </QueryClientProvider>
  );
}

/**
 * Read the typed api client from React context. Throws if called outside
 * `<ApiProvider>` — this is a programmer error, not a recoverable state.
 */
export function useApiClient(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error("useApiClient() must be called inside <ApiProvider>");
  }
  return ctx.client;
}

/**
 * Read the baseUrl + tokenProvider used to construct the api client.
 * Exposed for the handful of hooks that need to drive the XHR
 * `uploadMultipart` helper in `client.ts` — they can't route through
 * `openapi-fetch` because it doesn't surface upload-progress events.
 *
 * ALL non-multipart hook paths should use `useApiClient()` instead.
 */
export function useApiContext(): {
  client: ApiClient;
  baseUrl: string;
  tokenProvider: TokenProvider;
} {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error("useApiContext() must be called inside <ApiProvider>");
  }
  return ctx;
}
